<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { fetchMarketplaceJson as fetchMarketplace } from 'dashboard/helpers/marketplaceApi';

const route = useRoute();
const accountId = route.params.accountId;

/* ------------------------------------------------------------------ tarih */
// Ekip Karnesi ile aynı kalıp: toISOString() TR saatini bir gün geriye
// kaydırdığı için tarih YEREL olarak biçimlenir.
const fmt = d =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
const startOfDay = d => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

const PRESETS = [
  { key: 'today', label: 'Bugün' },
  { key: 'yesterday', label: 'Dün' },
  { key: 'this_week', label: 'Bu Hafta' },
  { key: 'last_week', label: 'Geçen Hafta' },
  { key: 'last_7', label: 'Son 7 Gün' },
  { key: 'last_30', label: 'Son 30 Gün' },
  { key: 'this_month', label: 'Bu Ay' },
  { key: 'last_month', label: 'Geçen Ay' },
];

const presetRange = key => {
  const t = new Date();
  let start = startOfDay(t);
  let end = startOfDay(t);
  if (key === 'yesterday') {
    start = addDays(start, -1);
    end = addDays(end, -1);
  } else if (key === 'this_week') {
    start = addDays(start, -((t.getDay() + 6) % 7));
  } else if (key === 'last_week') {
    const mon = addDays(startOfDay(t), -((t.getDay() + 6) % 7));
    start = addDays(mon, -7);
    end = addDays(mon, -1);
  } else if (key === 'this_month') {
    start = new Date(t.getFullYear(), t.getMonth(), 1);
  } else if (key === 'last_month') {
    start = new Date(t.getFullYear(), t.getMonth() - 1, 1);
    end = new Date(t.getFullYear(), t.getMonth(), 0);
  } else if (key === 'last_7') {
    start = addDays(start, -6);
  } else if (key === 'last_30') {
    start = addDays(start, -29);
  }
  return { start: fmt(start), end: fmt(end) };
};

/* ------------------------------------------------------------------ durum */
const preset = ref('last_30');
const startDate = ref(presetRange('last_30').start);
const endDate = ref(presetRange('last_30').end);
const draftStart = ref(startDate.value);
const draftEnd = ref(endDate.value);
const showCustom = ref(false);

const loading = ref(true);
const errorMsg = ref('');
const data = ref(null);
const selectedInbox = ref('all');

const CHANNEL_ICON = {
  'Channel::Instagram': '📷',
  'Channel::Whatsapp': '💬',
  'Channel::FacebookPage': '👍',
  'Channel::WebWidget': '🌐',
  'Channel::Api': '📨',
  'Channel::Email': '✉️',
};
const icon = t => CHANNEL_ICON[t] || '📥';

const GUNLER = {
  1: 'Pazartesi',
  2: 'Salı',
  3: 'Çarşamba',
  4: 'Perşembe',
  5: 'Cuma',
  6: 'Cumartesi',
  0: 'Pazar',
};

const load = async () => {
  loading.value = true;
  errorMsg.value = '';
  try {
    data.value = await fetchMarketplace(
      accountId,
      `reports/inbox_traffic?start=${startDate.value}&end=${endDate.value}`
    );
    if (
      selectedInbox.value !== 'all' &&
      !data.value.inboxes.some(i => i.id === selectedInbox.value)
    ) {
      selectedInbox.value = 'all';
    }
  } catch (e) {
    data.value = null;
    errorMsg.value =
      'Trafik verisi yüklenemedi. Bağlantıyı kontrol edip tekrar deneyin.';
  }
  loading.value = false;
};

const applyPreset = key => {
  preset.value = key;
  showCustom.value = false;
  const r = presetRange(key);
  startDate.value = r.start;
  endDate.value = r.end;
  load();
};

const applyCustom = () => {
  if (!draftStart.value || !draftEnd.value) return;
  preset.value = 'custom';
  startDate.value = draftStart.value;
  endDate.value = draftEnd.value;
  showCustom.value = false;
  load();
};

/* --------------------------------------------------------------- türetmeler */
const inboxes = computed(() => data.value?.inboxes || []);
const totals = computed(() => data.value?.totals || {});

const periodLabel = computed(() => {
  if (!data.value) return '';
  const { start, end, days } = data.value.range;
  const tr = s => s.split('-').reverse().join('.');
  return start === end ? tr(start) : `${tr(start)} – ${tr(end)} (${days} gün)`;
});

// Saat/gün tabloları seçili kanala göre süzülür; "all" seçiliyken toplam.
const pick = row =>
  selectedInbox.value === 'all'
    ? row.total
    : row.by_inbox?.[selectedInbox.value] || 0;

const hourly = computed(() =>
  (data.value?.hourly || []).map(r => ({ ...r, value: pick(r) }))
);
const weekday = computed(() =>
  (data.value?.weekday || []).map(r => ({ ...r, value: pick(r) }))
);

const hourlyMax = computed(() =>
  Math.max(1, ...hourly.value.map(r => r.value))
);
const weekdayMax = computed(() =>
  Math.max(1, ...weekday.value.map(r => r.value))
);
const filteredTotal = computed(() =>
  hourly.value.reduce((a, r) => a + r.value, 0)
);

const peakHours = computed(() =>
  [...hourly.value]
    .sort((a, b) => b.value - a.value)
    .slice(0, 3)
    .filter(r => r.value > 0)
);
const quietHours = computed(() =>
  [...hourly.value].sort((a, b) => a.value - b.value).slice(0, 3)
);

const selectedLabel = computed(() => {
  if (selectedInbox.value === 'all') return 'Tüm kanallar';
  const found = inboxes.value.find(i => i.id === selectedInbox.value);
  return found ? found.short_name : 'Tüm kanallar';
});

const nf = n => Number(n || 0).toLocaleString('tr-TR');
const pad = h => `${String(h).padStart(2, '0')}:00`;

// Yoğunluk rengi: sayı büyüdükçe koyulaşan tek renk skalası.
const heatClass = ratio => {
  if (ratio >= 0.85) return 'bg-woot-600';
  if (ratio >= 0.6) return 'bg-woot-500';
  if (ratio >= 0.35) return 'bg-woot-400';
  if (ratio > 0) return 'bg-woot-300';
  return 'bg-n-slate-4';
};

const exportCsv = () => {
  if (!data.value) return;
  const lines = [
    `Kanal & Saat Trafiği;${periodLabel.value}`,
    '',
    'Kanal;Yeni sohbet;Gelen mesaj;Farklı müşteri;Günlük ortalama;Pay %',
    ...inboxes.value.map(i =>
      [
        i.short_name,
        i.conversations,
        i.messages_in,
        i.contacts,
        i.per_day,
        i.share,
      ].join(';')
    ),
    '',
    `Saat dağılımı (${selectedLabel.value});Yeni sohbet`,
    ...hourly.value.map(r => `${pad(r.hour)};${r.value}`),
    '',
    `Gün dağılımı (${selectedLabel.value});Yeni sohbet`,
    ...weekday.value.map(r => `${GUNLER[r.dow]};${r.value}`),
  ];
  // Excel'in Türkçe karakterleri doğru açması için BOM.
  const blob = new Blob([`\uFEFF${lines.join('\n')}`], {
    type: 'text/csv;charset=utf-8;',
  });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `kanal-trafigi-${startDate.value}_${endDate.value}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
};

watch(showCustom, v => {
  if (v) {
    draftStart.value = startDate.value;
    draftEnd.value = endDate.value;
  }
});

onMounted(load);
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto bg-n-background">
    <div class="max-w-6xl w-full mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <!-- ============================ BAŞLIK ============================ -->
      <div class="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div class="min-w-0">
          <div
            class="text-xs font-bold uppercase tracking-widest text-n-slate-10"
          >
            Müşteri Hizmetleri
          </div>
          <h1
            class="text-3xl sm:text-4xl font-bold text-n-slate-12 mt-1 leading-tight"
          >
            📊 Kanal & Saat Trafiği
          </h1>
          <div
            class="text-base font-bold text-woot-600 dark:text-woot-400 mt-2"
          >
            📅 {{ periodLabel }}
          </div>
        </div>

        <div class="flex items-center gap-2">
          <button
            class="rounded-lg border border-n-weak px-3 py-2 text-sm font-semibold text-n-slate-11 hover:bg-n-solid-2 focus:outline-none focus:ring-2 focus:ring-woot-500 disabled:opacity-50"
            :disabled="loading"
            @click="load"
          >
            {{ loading ? '⏳ Yükleniyor…' : '🔄 Yenile' }}
          </button>
          <button
            class="rounded-lg border border-n-weak px-3 py-2 text-sm font-semibold text-n-slate-11 hover:bg-n-solid-2 focus:outline-none focus:ring-2 focus:ring-woot-500 disabled:opacity-50"
            :disabled="!inboxes.length"
            @click="exportCsv"
          >
            ⬇️ Excel
          </button>
        </div>
      </div>

      <!-- =========================== TARİH FİLTRESİ =========================== -->
      <div class="flex flex-wrap items-center gap-2 mb-6">
        <button
          v-for="p in PRESETS"
          :key="p.key"
          class="rounded-lg px-3 py-1.5 text-sm font-semibold border"
          :class="
            preset === p.key
              ? 'bg-woot-600 border-woot-600 text-white'
              : 'border-n-weak text-n-slate-11 hover:bg-n-solid-2'
          "
          @click="applyPreset(p.key)"
        >
          {{ p.label }}
        </button>
        <button
          class="rounded-lg px-3 py-1.5 text-sm font-semibold border"
          :class="
            preset === 'custom'
              ? 'bg-woot-600 border-woot-600 text-white'
              : 'border-n-weak text-n-slate-11 hover:bg-n-solid-2'
          "
          @click="showCustom = !showCustom"
        >
          🗓️ Tarih seç
        </button>

        <div v-if="showCustom" class="flex items-center gap-2 w-full sm:w-auto">
          <input
            v-model="draftStart"
            type="date"
            class="rounded-lg border border-n-weak bg-n-background px-2 py-1.5 text-sm text-n-slate-12"
          />
          <span class="text-n-slate-10">–</span>
          <input
            v-model="draftEnd"
            type="date"
            class="rounded-lg border border-n-weak bg-n-background px-2 py-1.5 text-sm text-n-slate-12"
          />
          <button
            class="rounded-lg bg-woot-600 px-3 py-1.5 text-sm font-semibold text-white"
            @click="applyCustom"
          >
            Uygula
          </button>
        </div>
      </div>

      <div
        v-if="errorMsg"
        class="rounded-lg border border-n-ruby-6 bg-n-ruby-2 px-4 py-3 text-sm text-n-ruby-11 mb-6"
      >
        {{ errorMsg }}
      </div>

      <div v-if="loading" class="text-n-slate-10 text-sm py-10">
        Veriler hazırlanıyor…
      </div>

      <template v-else-if="data">
        <!-- ============================ ÖZET ============================ -->
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-7">
          <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
            <div class="text-xs font-semibold uppercase text-n-slate-10">
              Yeni sohbet
            </div>
            <div class="text-2xl font-bold text-n-slate-12 mt-1">
              {{ nf(totals.conversations) }}
            </div>
            <div class="text-xs text-n-slate-10 mt-1">
              günde ~{{ nf(totals.per_day) }}
            </div>
          </div>
          <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
            <div class="text-xs font-semibold uppercase text-n-slate-10">
              Gelen mesaj
            </div>
            <div class="text-2xl font-bold text-n-slate-12 mt-1">
              {{ nf(totals.messages_in) }}
            </div>
            <div class="text-xs text-n-slate-10 mt-1">müşteriden gelen</div>
          </div>
          <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
            <div class="text-xs font-semibold uppercase text-n-slate-10">
              Farklı müşteri
            </div>
            <div class="text-2xl font-bold text-n-slate-12 mt-1">
              {{ nf(totals.contacts) }}
            </div>
            <div class="text-xs text-n-slate-10 mt-1">tekil kişi</div>
          </div>
          <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
            <div class="text-xs font-semibold uppercase text-n-slate-10">
              En yoğun saat
            </div>
            <div class="text-2xl font-bold text-n-slate-12 mt-1">
              {{ peakHours.length ? pad(peakHours[0].hour) : '—' }}
            </div>
            <div class="text-xs text-n-slate-10 mt-1">
              {{ peakHours.length ? `${nf(peakHours[0].value)} sohbet` : '' }}
            </div>
          </div>
        </div>

        <!-- ============================ KANALLAR ============================ -->
        <h2 class="text-lg font-bold text-n-slate-12 mb-3">
          Hangi kanaldan kaç sohbet
        </h2>
        <div
          class="rounded-xl border border-n-weak bg-n-solid-1 overflow-x-auto mb-8"
        >
          <table class="w-full text-sm min-w-[640px]">
            <thead>
              <tr class="text-xs uppercase text-n-slate-10">
                <th class="text-left font-semibold px-4 py-3">Kanal</th>
                <th class="text-right font-semibold px-4 py-3">Yeni sohbet</th>
                <th class="text-right font-semibold px-4 py-3">Gelen mesaj</th>
                <th class="text-right font-semibold px-4 py-3">
                  Farklı müşteri
                </th>
                <th class="text-right font-semibold px-4 py-3">Günlük ort.</th>
                <th class="text-left font-semibold px-4 py-3 w-40">Pay</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="i in inboxes"
                :key="i.id"
                class="border-t border-n-weak cursor-pointer hover:bg-n-solid-2"
                :class="selectedInbox === i.id ? 'bg-n-solid-2' : ''"
                @click="selectedInbox = selectedInbox === i.id ? 'all' : i.id"
              >
                <td class="px-4 py-3 font-semibold text-n-slate-12">
                  {{ icon(i.channel_type) }} {{ i.short_name }}
                </td>
                <td class="px-4 py-3 text-right tabular-nums text-n-slate-12">
                  {{ nf(i.conversations) }}
                </td>
                <td class="px-4 py-3 text-right tabular-nums text-n-slate-11">
                  {{ nf(i.messages_in) }}
                </td>
                <td class="px-4 py-3 text-right tabular-nums text-n-slate-11">
                  {{ nf(i.contacts) }}
                </td>
                <td class="px-4 py-3 text-right tabular-nums text-n-slate-11">
                  {{ nf(i.per_day) }}
                </td>
                <td class="px-4 py-3">
                  <div class="flex items-center gap-2">
                    <div class="h-2 flex-1 rounded bg-n-slate-4">
                      <div
                        class="h-2 rounded bg-woot-500"
                        :style="{ width: `${i.share}%` }"
                      />
                    </div>
                    <span class="text-xs text-n-slate-10 w-12 text-right">
                      %{{ i.share }}
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- ======================= SAAT / GÜN DAĞILIMI ======================= -->
        <div class="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h2 class="text-lg font-bold text-n-slate-12">
            Saat ve gün dağılımı
          </h2>
          <div class="flex flex-wrap items-center gap-2">
            <button
              class="rounded-lg px-3 py-1.5 text-xs font-semibold border"
              :class="
                selectedInbox === 'all'
                  ? 'bg-woot-600 border-woot-600 text-white'
                  : 'border-n-weak text-n-slate-11 hover:bg-n-solid-2'
              "
              @click="selectedInbox = 'all'"
            >
              Tüm kanallar
            </button>
            <button
              v-for="i in inboxes"
              :key="`f-${i.id}`"
              class="rounded-lg px-3 py-1.5 text-xs font-semibold border"
              :class="
                selectedInbox === i.id
                  ? 'bg-woot-600 border-woot-600 text-white'
                  : 'border-n-weak text-n-slate-11 hover:bg-n-solid-2'
              "
              @click="selectedInbox = i.id"
            >
              {{ icon(i.channel_type) }} {{ i.short_name }}
            </button>
          </div>
        </div>

        <div
          class="rounded-xl border border-n-weak bg-n-solid-1 px-4 py-3 mb-4 text-sm text-n-slate-11"
        >
          <b class="text-n-slate-12">{{ selectedLabel }}</b> ·
          {{ nf(filteredTotal) }} yeni sohbet ·
          <template v-if="peakHours.length">
            en yoğun
            <b class="text-n-slate-12">
              {{ peakHours.map(h => pad(h.hour)).join(', ') }}
            </b>
            · en sakin
            <b class="text-n-slate-12">
              {{ quietHours.map(h => pad(h.hour)).join(', ') }}
            </b>
          </template>
        </div>

        <div class="grid lg:grid-cols-3 gap-4 mb-10">
          <div
            class="lg:col-span-2 rounded-xl border border-n-weak bg-n-solid-1 p-4"
          >
            <div class="text-xs font-semibold uppercase text-n-slate-10 mb-3">
              Saat saat (Türkiye saati)
            </div>
            <div class="space-y-1">
              <div
                v-for="r in hourly"
                :key="r.hour"
                class="flex items-center gap-3"
              >
                <span class="w-12 text-xs tabular-nums text-n-slate-10">
                  {{ pad(r.hour) }}
                </span>
                <div class="flex-1 h-4 rounded bg-n-slate-3 overflow-hidden">
                  <div
                    class="h-4 rounded"
                    :class="heatClass(r.value / hourlyMax)"
                    :style="{
                      width: `${Math.max((r.value / hourlyMax) * 100, r.value ? 2 : 0)}%`,
                    }"
                  />
                </div>
                <span
                  class="w-12 text-right text-xs tabular-nums font-semibold text-n-slate-12"
                >
                  {{ nf(r.value) }}
                </span>
              </div>
            </div>
          </div>

          <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
            <div class="text-xs font-semibold uppercase text-n-slate-10 mb-3">
              Haftanın günü
            </div>
            <div class="space-y-2">
              <div
                v-for="r in weekday"
                :key="r.dow"
                class="flex items-center gap-3"
              >
                <span class="w-20 text-xs text-n-slate-10">
                  {{ GUNLER[r.dow] }}
                </span>
                <div class="flex-1 h-4 rounded bg-n-slate-3 overflow-hidden">
                  <div
                    class="h-4 rounded"
                    :class="heatClass(r.value / weekdayMax)"
                    :style="{
                      width: `${Math.max((r.value / weekdayMax) * 100, r.value ? 2 : 0)}%`,
                    }"
                  />
                </div>
                <span
                  class="w-12 text-right text-xs tabular-nums font-semibold text-n-slate-12"
                >
                  {{ nf(r.value) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- ============================ AÇIKLAMA ============================ -->
        <div
          class="rounded-xl border border-n-weak bg-n-solid-1 p-4 text-sm text-n-slate-11 leading-relaxed mb-10"
        >
          <b class="text-n-slate-12">Nasıl okunur:</b>
          "Yeni sohbet", o kanalda yeni açılan konuşma sayısıdır; aynı müşteri
          aynı konuşmada tekrar yazarsa yeniden sayılmaz. "Gelen mesaj",
          müşteriden gelen tek tek mesajlardır. Saat ve gün sütunları sohbetin
          <b class="text-n-slate-12">açıldığı</b> anı gösterir, cevaplandığı anı
          değil. Kanal satırına tıklayarak saat ve gün dağılımını yalnızca o
          kanal için görebilirsiniz.
        </div>
      </template>
    </div>
  </div>
</template>
